// 🖋️ Rich text (v129.1, Matt: "can we look at rich text formatting?
// specifically in e-mail, tasks, news and policies?" → "yes do it everywhere")
//
// ONE shared markdown-light layer for every compose box + every render site:
//   **bold**   *italic*   lines starting "- " or "• " become bullets
//   bare URLs auto-link (https only, target=_blank rel=noopener)
//
// SAFETY MODEL (load-bearing — this app is pay-critical and XSS-pinned):
//   - Text is STORED as plain text with markers. No user HTML, ever.
//   - formatRich() escapes EVERYTHING first (&<>"'), then applies markers to
//     the ESCAPED text — so the only HTML in the output is ours.
//   - The renderer is pure + deterministic; both apps load this one bundle
//     (the src/version precedent) so there is exactly ONE copy of the rules.
//
// Classic-script contract: esbuild globalName clobbers window.RichText — every
// onclick-reachable handler is in the export list at the bottom.

function escAll(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// URL matcher on ESCAPED text — conservative: https only, no trailing punct.
var URL_RE = /(https?:\/\/[^\s&]+(?:&amp;[^\s&]+)*)/g;

function linkify(escaped) {
  return escaped.replace(URL_RE, function (u) {
    var trimmed = u.replace(/[.,)\]!?]+$/, '');
    var tail = u.slice(trimmed.length);
    // the href is the escaped URL — quotes/angles are already entities
    return '<a href="' + trimmed + '" target="_blank" rel="noopener">' + trimmed + '</a>' + tail;
  });
}

function inline(escaped) {
  // **bold** then *italic* — non-greedy, no nesting ambitions
  return linkify(escaped)
    .replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<i>$2</i>');
}

// text → safe HTML. opts.breaks (default true) turns \n into <br>.
function formatRich(text, opts) {
  opts = opts || {};
  var breaks = opts.breaks !== false;
  var lines = escAll(text).split('\n');
  var out = [];
  var list = null;
  for (var i = 0; i < lines.length; i++) {
    var l = lines[i];
    var m = /^\s*(?:-|•)\s+(.*)$/.exec(l);
    if (m) {
      if (!list) { list = []; }
      list.push('<li>' + inline(m[1]) + '</li>');
      continue;
    }
    if (list) { out.push('<ul style="margin:4px 0 4px 18px;padding:0">' + list.join('') + '</ul>'); list = null; }
    out.push(inline(l));
  }
  if (list) out.push('<ul style="margin:4px 0 4px 18px;padding:0">' + list.join('') + '</ul>');
  // join: <ul> blocks are their own lines; plain lines joined with <br> (or \n)
  var html = '';
  for (var j = 0; j < out.length; j++) {
    var chunk = out[j];
    if (j > 0 && out[j - 1].indexOf('<ul') !== 0 && chunk.indexOf('<ul') !== 0) html += breaks ? '<br>' : '\n';
    html += chunk;
  }
  return html;
}

// True when the text uses any marker — lets render sites keep their old plain
// path for legacy content byte-identical (belt and braces; formatRich on
// marker-free text is equivalent anyway).
function hasMarkers(text) {
  var s = String(text == null ? '' : text);
  return /\*\*[^*\n]+\*\*|(^|[^*])\*[^*\n]+\*(?!\*)|^\s*(?:-|•)\s+/m.test(s) || /https?:\/\//.test(s);
}

// ── the compose toolbar (B / i / • / 🔗) ────────────────────────────────────
function toolbarHtml(targetId) {
  var btn = function (label, fn, title) {
    return '<button type="button" title="' + title + '" onclick="RichText.' + fn + '(\'' + targetId + '\')" ' +
      'style="border:1px solid var(--border-strong);background:var(--card);border-radius:7px;min-width:30px;padding:3px 8px;font-size:12.5px;cursor:pointer;font-family:inherit">' + label + '</button>';
  };
  return '<div style="display:flex;gap:6px;margin:4px 0">' +
    btn('<b>B</b>', 'bold', 'Bold — **text**') +
    btn('<i>i</i>', 'italic', 'Italic — *text*') +
    btn('•', 'bullet', 'Bullet line') +
    btn('🔗', 'link', 'Insert a link') +
  '</div>';
}

function _wrapSelection(id, mark) {
  var el = document.getElementById(id);
  if (!el) return;
  var s = el.selectionStart || 0, e = el.selectionEnd || 0;
  var v = el.value;
  var sel = v.slice(s, e) || 'text';
  el.value = v.slice(0, s) + mark + sel + mark + v.slice(e);
  el.focus();
  el.setSelectionRange(s + mark.length, s + mark.length + sel.length);
}

function bold(id) { _wrapSelection(id, '**'); }
function italic(id) { _wrapSelection(id, '*'); }

function bullet(id) {
  var el = document.getElementById(id);
  if (!el) return;
  var s = el.selectionStart || 0;
  var v = el.value;
  var lineStart = v.lastIndexOf('\n', s - 1) + 1;
  el.value = v.slice(0, lineStart) + '- ' + v.slice(lineStart);
  el.focus();
  el.setSelectionRange(s + 2, s + 2);
}

function link(id) {
  var el = document.getElementById(id);
  if (!el) return;
  var s = el.selectionStart || 0, e = el.selectionEnd || 0;
  var v = el.value;
  var sel = v.slice(s, e);
  var ins = /^https?:\/\//.test(sel) ? sel : (sel ? sel + ' https://' : 'https://');
  el.value = v.slice(0, s) + ins + v.slice(e);
  el.focus();
  el.setSelectionRange(s + ins.length, s + ins.length);
}

// inline markers + links on ALREADY-ESCAPED text — the policies reader keeps
// its own paragraph model and calls this per paragraph.
function applyInline(escapedText) { return inline(String(escapedText == null ? '' : escapedText)); }

export { formatRich, hasMarkers, toolbarHtml, bold, italic, bullet, link, applyInline };

// pure formatRich is importable by the Worker too — guard the DOM binding
if (typeof window !== 'undefined') window.formatRich = formatRich;
